import { DatePicker, Input, Radio, Form } from "antd";
import TextArea from "antd/lib/input/TextArea";
import * as React from "react";
import moment from "moment";
import { FormInstance } from "antd/lib/form";
import { TableAPI } from "../../API/TableAPI";
import { makeEntryModal } from "./CreateEntryModals";
import Entry from "../../Model/Entry";

function renderForm(form: FormInstance) {
  return (
    <Form className="modal-form" hideRequiredMark={true} form={form}>
      <Form.Item
        name="parameter"
        label="Parameter"
        rules={[{ required: true, message: "Parameter is required" }]}
      >
        <Input placeholder="ex. Aerosol Optical Depth" />
      </Form.Item>
      <Form.Item name="network" label="Network Name" required>
        <Input />
      </Form.Item>
      <Form.Item
        name="instrument"
        label="Instrument"
        help="For multiple instruments of the same network create new entries"
        required
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="isWavelengthApplicable"
        label="Is wavelength applicable to this parameter?"
      >
        <Radio.Group buttonStyle="solid">
          <Radio.Button value="yes">Yes</Radio.Button>
          <Radio.Button value="no">No</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev, current) =>
          prev.isWavelengthApplicable === current.isWavelengthApplicable
        }
      >
        {({ getFieldValue }) =>
          getFieldValue("isWavelengthApplicable") ? (
            <Form.Item
              name="spectralRange"
              label="Wavelength (nm)"
              help="For multiple values, you can input ranges (532-600) or comma-separated values (532, 628, 1024)"
            >
              <Input placeholder="ex. 532 nm" />
            </Form.Item>
          ) : null
        }
      </Form.Item>
      <Form.Item
        name="unit"
        label="Unit"
        rules={[
          { required: true, message: "If not applicable, use 'Unitless'" },
        ]}
      >
        <Input placeholder="ex. km-1 sr-1" />
      </Form.Item>
      <Form.Item
        name="temporalResolution"
        label="Temporal Resolution"
        rules={[{ required: true, message: "This field is not optional" }]}
      >
        <Input placeholder="ex. Daily, Sub-daily, 5-min, ..." />
      </Form.Item>
      <Form.Item
        name="verticalResolution"
        label="Vertical Resolution"
        rules={[{ required: true, message: "This field is not optional" }]}
      >
        <Input placeholder="ex. Columnar, 30-180m, ..." />
      </Form.Item>
      <Form.Item
        name="coveredRegion"
        label="Covered Region"
        rules={[
          { required: true, message: "This field is not optional" },
          { pattern: /[A-Z].*/g, message: "Please use capitalised words" },
        ]}
      >
        <Input placeholder="ex. Global, Europe, ..." />
      </Form.Item>
      <Form.Item name="isNetworkActive" label="Is the network still active?">
        <Radio.Group buttonStyle="solid">
          <Radio.Button value={true}>Yes</Radio.Button>
          <Radio.Button value={false}>No</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        name="dataAvailabilityFrom"
        label="Data available from"
        rules={[{ required: true, message: "This field is not optional" }]}
      >
        <DatePicker format="YYYY-MM-DD" />
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev, current) =>
          prev.isNetworkActive !== current.isNetworkActive
        }
      >
        {({ getFieldValue }) =>
          getFieldValue("isNetworkActive") === false ? (
            <Form.Item
              name="dataAvailabilityTo"
              label="Data available to"
              help="Leave empty for satellites that are still active"
            >
              <DatePicker format="YYYY-MM-DD" />
            </Form.Item>
          ) : null
        }
      </Form.Item>
      <Form.Item name="NRT_RRT" label="NRT/RRT Availability">
        <Radio.Group buttonStyle="solid">
          <Radio.Button value={0}>Yes</Radio.Button>
          <Radio.Button value={1}>Partially</Radio.Button>
          <Radio.Button value={2}>No</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        name="QAProgram"
        label="QA Program"
        rules={[
          { type: "url", message: "Must be a valid URL" },
          { required: true, message: "This field is not optional" },
        ]}
      >
        <Input
          type="url"
          placeholder="Include 'http://', 'https://', 'ftp://' or 'sftp://'"
        />
      </Form.Item>
      <Form.Item
        name="dataFormat"
        label="Data format"
        rules={[{ required: true, message: "This field is not optional" }]}
      >
        <Input placeholder="ex. NetCDF, Binary, Text, HDF5, ..." />
      </Form.Item>
      <Form.Item
        name="mapURL"
        label="Map View"
        rules={[
          { type: "url", message: "Must be a valid URL" },
          { required: true, message: "This field is not optional" },
        ]}
      >
        <Input
          type="url"
          placeholder="Include 'http://', 'https://', 'ftp://' or 'sftp://'"
        />
      </Form.Item>
      <Form.Item
        name="productURL"
        label="Product View"
        rules={[
          { type: "url", message: "Must be a valid URL" },
          { required: true, message: "This field is not optional" },
        ]}
      >
        <Input
          type="url"
          placeholder="Include 'http://', 'https://', 'ftp://' or 'sftp://'"
        />
      </Form.Item>
      <Form.Item
        name="dataURL"
        label="Data View"
        rules={[
          { type: "url", message: "Must be a valid URL" },
          { required: true, message: "This field is not optional" },
        ]}
      >
        <Input
          type="url"
          placeholder="Include 'http://', 'https://', 'ftp://' or 'sftp://'"
        />
      </Form.Item>
      <Form.Item name="references" label="References" required>
        <TextArea />
      </Form.Item>
      <Form.Item name="institutionData" label="Responsible Institution">
        <Input />
      </Form.Item>
    </Form>
  );
}

export function formToEntry(form: FormInstance) {
  // Format dates
  const dataAvailabilityFromRaw = form.getFieldValue("dataAvailabilityFrom");
  const dataAvailabilityFrom =
    dataAvailabilityFromRaw === undefined
      ? ""
      : dataAvailabilityFromRaw.format("YYYY-MM-DD");

  let dataAvailabilityTo = null;
  if (!form.getFieldValue("isNetworkActive")) {
    const dataAvailabilityToRaw = form.getFieldValue("dataAvailabilityTo");
    dataAvailabilityTo =
      dataAvailabilityToRaw === undefined
        ? ""
        : dataAvailabilityToRaw.format("YYYY-MM-DD");
  }

  // Create body
  return {
    parameter: form.getFieldValue("parameter"),
    network: form.getFieldValue("network"),
    instrument: form.getFieldValue("instrument"),
    spectralRange: (form.getFieldValue("isWavelendthApplicaple") as boolean)
      ? form.getFieldValue("spectralRange")
      : "N/A",
    unit: form.getFieldValue("unit"),
    temporalResolution: form.getFieldValue("temporalResolution"),
    verticalResolution: form.getFieldValue("verticalResolution"),
    coveredRegion: form.getFieldValue("coveredRegion"),
    dataAvailabilityFrom,
    dataAvailabilityTo,
    NRT_RRT: form.getFieldValue("NRT_RRT"),
    QAProgram: form.getFieldValue("QAProgram"),
    dataFormat: form.getFieldValue("dataFormat"),
    mapURL: form.getFieldValue("mapURL"),
    productURL: form.getFieldValue("productURL"),
    dataURL: form.getFieldValue("dataURL"),
    references: form.getFieldValue("references"),
    institutionData: form.getFieldValue("institutionData"),
  };
}

function entryToForm(form: FormInstance, entry: Entry) {
  // Handle spectralRange checkbox
  let spectralRange = entry.SpectralRange;
  if (spectralRange === "N/A") {
    spectralRange = "";
    form.setFieldsValue({
      isWavelengthApplicable: false,
    });
  } else {
    form.setFieldsValue({
      isWavelengthApplicable: true,
    });
  }

  // Parse date
  const momentFrom = moment(entry.dataAvailabilityFrom, "YYYY-MM-DD");

  let momentTo: moment.Moment | null;
  if (entry.dataAvailabilityTo === null) {
    momentTo = null;
    form.setFieldsValue({ isNetworkActive: true });
  } else {
    momentTo = moment(entry.dataAvailabilityTo, "YYYY-MM-DD");
    form.setFieldsValue({ isNetworkActive: false });
  }

  form.setFieldsValue({
    ...entry,
    dataAvailabilityFrom: momentFrom,
    dataAvailabilityTo: momentTo,
  });
}

export const CreateNetworkModal = makeEntryModal(
  "ground-based network",
  new TableAPI("groundBased"),
  renderForm,
  formToEntry,
  entryToForm
);
